import React, { useState, useEffect } from "react";
import { Box, Select, InputLabel, MenuItem, FormControlLabel, FormGroup, Button, Typography, SelectChangeEvent, Checkbox, Divider, Grid, Stack } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import GenericDialog from "../../../components/shared/GenericDialog";
import ConfigLoad from "../../../components/shared/config/ConfigLoad";
import { IDocumentBundle, IDocumentHandlingPackageConfig, IMergerPackageConfig } from "../../../models/configuration/bundles/IBundles";
import DTDataGrid from "../../../components/shared/config/DTDataGrid";
import { useDocumentMappings } from "../../../hooks/useDocumentMappings";
import { mergePackageConfigColumns, mergePackageConfigColumnsRef, packageConfigColumns, packageConfigColumnsRef } from "./configbundlingdata";
import { DocumentMapsPrefixType } from "../../../enums/BundlingTypes";
import { AlertSeverity } from "../../../constants/AlertTypes";
import { stripTempIds, generateTempId } from "../../../utils/dataUtils";
import {
    dialogStyles,
    StyledCloseButton,
    StyledMainContainer,
    StyledFormRow,
    StyledFieldContainer,
    StyledPaddedFieldContainer,
    StyledPackageIdField,
    StyledCheckbox,
    StyledFormControl,
    StyledCard,
    StyledCardContent,
    StyledDataGridContainer,
    dataGridStyles,
    StyledCardActions,
    StyledButtonContainer,
    StyledWarningText,
    StyledSnackbar,
    StyledAlert,
    titleSx,
    containerSx
} from "./ConfigDocumentHandlingDialog.styles";
import { DtDialog } from "../../../components/ui/dialog";
import { DtDataGrid } from "../../../components/ui/dataGrid";
import { Edit, TramSharp } from "@mui/icons-material";
import DtAlert from "../../../components/ui/alert/DtAlert";
import { DtHeader } from "../../../components/ui/header";

interface ConfigDocumentHandlingDialogProps {
    open: boolean;
    documentBundle: IDocumentBundle;
    updateBundle: (bundle: IDocumentBundle) => void;
    closeFxtn: () => void;
}

interface IBundlingValidator {
    error: boolean;
    raise: boolean;
    message: string;
    title: string;
}

const OneFolderPerDocument = "OneFolderPerDocument";
const OneFolderPerPackage = "OneFolderPerPackage";
const TitleOneFolderPerDocument = "Document Handling Package Configs";
const TitleOneFolderPerPackage = "Merger Package Configs";

const ConfigDocumentHandlingDialog: React.FC<ConfigDocumentHandlingDialogProps> = ({ open, documentBundle, updateBundle, closeFxtn }) => {

    const [documentBundleState, setDocumentBundleState] = useState<IDocumentBundle>(documentBundle);
    const [title, setTitle] = useState<string>(TitleOneFolderPerDocument);
    const [isLoading, setIsLoading] = useState(false);

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    
    const {data:documentMappings}= useDocumentMappings();
    const documentMapsPrefix = [DocumentMapsPrefixType.ALL, DocumentMapsPrefixType.AUDIT_LOG];

    useEffect(() => {
        const bundleWithIds = { ...documentBundle };
        
        // Add temporary IDs to existing rows if they don't have them
        if (bundleWithIds.DocumentHandlingPackageConfigs) {
            bundleWithIds.DocumentHandlingPackageConfigs = bundleWithIds.DocumentHandlingPackageConfigs.map(config => 
                config.__tempId ? config : { 
                    ...config, 
                    __tempId: generateTempId()
                }
            );
        }
        
        if (bundleWithIds.MergerPackageConfigs) {
            bundleWithIds.MergerPackageConfigs = bundleWithIds.MergerPackageConfigs.map(config =>
                config.__tempId ? config : { 
                    ...config, 
                    __tempId: generateTempId()
                }
            );
        }
        
        setDocumentBundleState(bundleWithIds);
    }, [documentBundle]);

    useEffect(() => {
        if (open) {
            setIsLoading(true);
            // Simulate loading time for dialog initialization
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [open]);

    // Additional effect to handle data dependency loading
    useEffect(() => {
        if (open && documentMappings) {
            setIsLoading(false);
        } else if (open && !documentMappings) {
            setIsLoading(true);
        }
    }, [open, documentMappings]);

    // Commits bundle changes to both local and parent state
    const commitBundleUpdate = (updater: (state: IDocumentBundle) => IDocumentBundle) => {
        const newState = updater(documentBundleState);
        setDocumentBundleState(newState);
        
        const validation = documentBundleStateValidated(newState);
        if (!validation.error) {
            // Only create expensive stripped copy when we actually need to persist
            updateBundle(stripTempIds(newState));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent) => {
        const { name, value } = e.target;
        setTitle(value === OneFolderPerPackage ? TitleOneFolderPerPackage : TitleOneFolderPerDocument);
        handlePropChange(name, value);
    };

    const handlePropChange = (name: string, value: any) => {
        commitBundleUpdate(state => ({
            ...state,
            [name]: value,
        }));
    };

  const addRow = (row: any) => {
    // Generate unique ID for new rows
    const uniqueId = generateTempId();
    const rowWithId = { ...row, __tempId: uniqueId };
    
    if (documentBundleState?.BundlingOption === OneFolderPerDocument) {
      const currentConfigs = documentBundleState?.DocumentHandlingPackageConfigs || [];
      commitBundleUpdate(state => ({
        ...state,
        DocumentHandlingPackageConfigs: [...currentConfigs, rowWithId],
      }));
    } else if (documentBundleState?.BundlingOption === OneFolderPerPackage) {
      const currentConfigs = documentBundleState?.MergerPackageConfigs || [];
      // Ensure MergeList is always an array
      const mergeList = Array.isArray(row.MergeList)
        ? row.MergeList
        : typeof row.MergeList === "string"
          ? row.MergeList.split(",").map((item: string) => item.trim()).filter((item: string | any[]) => item.length > 0)
          : [];
      commitBundleUpdate(state => ({
        ...state,
        MergerPackageConfigs: [...currentConfigs, { ...rowWithId, MergeList: mergeList }],
      }));
    }
  };

  const editRow = (row: any, rowIndex: number) => {
    if (documentBundleState?.BundlingOption === OneFolderPerDocument) {
      const updatedConfigs = documentBundleState.DocumentHandlingPackageConfigs.map((config, index) =>
        index === rowIndex ? { ...row } : config
      );
      commitBundleUpdate(state => ({
        ...state,
        DocumentHandlingPackageConfigs: updatedConfigs,
      }));
    } else if (documentBundleState?.BundlingOption === OneFolderPerPackage) {
      // Ensure MergeList is always an array
      const mergeList = Array.isArray(row.MergeList)
        ? row.MergeList
        : typeof row.MergeList === "string"
          ? row.MergeList.split(",").map((item: string) => item.trim()).filter((item: string | any[]) => item.length > 0)
          : [];
      const updatedConfigs = documentBundleState.MergerPackageConfigs.map((config, index) =>
        index === rowIndex ? { ...row, MergeList: mergeList } : config
      );
      commitBundleUpdate(state => ({
        ...state,
        MergerPackageConfigs: updatedConfigs,
      }));
    }
  };

  const removeRow = (row: any, rowIndex: number) => {
    
    if (!row.__tempId) {
      console.error(
        "ConfigDocumentHandlingDialog.removeRow: Missing __tempId for row at index", 
        rowIndex, 
        row
      );
      return;
    }
    
    if (documentBundleState?.BundlingOption === OneFolderPerDocument) {
      const updatedConfigs = documentBundleState.DocumentHandlingPackageConfigs.filter(
        (config) => config.__tempId !== row.__tempId
      );
      commitBundleUpdate(state => ({
        ...state,
        DocumentHandlingPackageConfigs: updatedConfigs,
      }));
    } else if (documentBundleState?.BundlingOption === OneFolderPerPackage) {
      const updatedConfigs = documentBundleState.MergerPackageConfigs.filter(
        (config) => config.__tempId !== row.__tempId
      );
      commitBundleUpdate(state => ({
        ...state,
        MergerPackageConfigs: updatedConfigs,
      }));
    }
  };

    const closeScreen = () => {
        if (documentBundleState != null) {
            const validation = documentBundleStateValidated(documentBundleState);
            if (validation.error) {
                if (validation.raise) {
                    openAlert(validation.message, 'error');
                } else {
                    closeFxtn();
                }
                return;
            }
            updateBundle(stripTempIds(documentBundleState));
            closeFxtn();
        }
    }

    // Validation Functions

    const createDefaultBundlingValidator = (message: string): IBundlingValidator =>
    ({
        error: false,
        raise: false,
        message: message || "",
        title: ""
    });

    const documentBundleStateValidated = (bundle: IDocumentBundle): IBundlingValidator => {
        const validator = createDefaultBundlingValidator("");

        const validateDHPC = validateDocumentHandlingPackageConfigs(bundle.DocumentHandlingPackageConfigs);

        if (validateDHPC.error) {
            return { ...validator, ...validateDHPC };
        }

        const validateMPC = validateMergerPackageConfigs(bundle.MergerPackageConfigs);
        if (validateMPC.error) {
            return { ...validator, ...validateMPC };
        }

        return validator;
    }

    const validateDocumentHandlingPackageConfigs = (configs: IDocumentHandlingPackageConfig[])
        : IBundlingValidator => {

        let validator = createDefaultBundlingValidator("Invalid Document Handling Package");
        if (!configs || configs.length === 0) return validator;
        
        // Don't auto-remove rows, just validate and report errors
        const hasInvalidRows = configs.some(config => config.DocumentIndex === null);
        if (hasInvalidRows) {
            validator.error = true;
            validator.raise = false; // Don't raise alert for empty rows
            validator.message = "Document Index is required for all rows";
        }
        
        return validator;
    }

    const validateMergerPackageConfigs = (configs: IMergerPackageConfig[])
        : IBundlingValidator => {
        let validator = createDefaultBundlingValidator("Invalid Merger Package");
        if (!configs || configs.length === 0) return validator;
        
        // Don't auto-remove rows, just validate and report errors
        const hasInvalidRows = configs.some(config => !config.MergeList?.length || config.MergeList.some(item => !item.length));
        if (hasInvalidRows) {
            validator.error = true;
            validator.raise = false; // Don't raise alert for empty rows
            validator.message = "Merge List is required for all rows";
        }
        
        // Check for empty folder when merge list has values
        configs.forEach((config) => {
            if ((config.MergeList ?? []).length > 0 && config.EncompassFolder === "") {
                validator.error = true;
                validator.raise = true;
                validator.message = "Encompass Folder is Mandatory";
                return validator;
            }
        });

        return validator;
    }

  const getCalculatedConfigValue = (
    item: IDocumentHandlingPackageConfig
) => {
    if (!item || !documentMappings?.Configuration?.DocumentMaps) return undefined;

    // Support both object and string for DocumentIndex
    const documentIndexValue = item.DocumentIndex?.toString();
    // Find new document map value
    let map = documentMappings.Configuration.DocumentMaps.find(
        (map) => map.DocumentIndex.toString() === documentIndexValue
    );

    // Set document names empty for documentMapsPrefix
    if (
        documentIndexValue === DocumentMapsPrefixType.AUDIT_LOG ||
        documentIndexValue === DocumentMapsPrefixType.ALL
    ) {
        map = {
            DocumentIndex: documentIndexValue,
            ConformXDocumentName: "",
            EncompassDocumentName: "",
        };
    }

    // Calculate the updated config value
    let calculatedValue = {};
    if (map) {
        // You can return an object or just the calculated field
        calculatedValue = {
            DocumentIndex: documentIndexValue,
            DocumentName: map.ConformXDocumentName,
            EncompassFolder: map.EncompassDocumentName,
        };
    }

    return calculatedValue;
};

    // Alert Handling 
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }

    if (documentBundleState == null) return <></>;

    if (documentMappings == null) return <></>;

    return (
      <>
        <ConfigLoad loading={isLoading} />
        <DtDialog
          maxWidth="xl"
          open={open}
          title="Document Handling Configuration"
          onClose={closeScreen}
          actions={ <Button size="small" variant="contained" onClick={closeScreen}>Close</Button> }
        >
          <Stack>
            <Grid container spacing={2}>

              {/* Top Row: Package ID, Bundling Option */}
              <Grid item xs={3}>
                <StyledPackageIdField
                  label="Package ID"
                  name="PackageID"
                  value={documentBundleState?.PackageId}
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={e => handlePropChange("PackageId", e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <StyledFormControl fullWidth sx={{ mt: 0.5 }}>
                  <InputLabel>Bundling Option</InputLabel>
                  <Select
                    label="Bundling Option"
                    name="BundlingOption"
                    value={documentBundleState?.BundlingOption || ''}
                    onChange={handleSelectChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value={OneFolderPerDocument}>One Folder Per Document</MenuItem>
                    <MenuItem value={OneFolderPerPackage}>One Folder Per Package</MenuItem>
                  </Select>
                </StyledFormControl>
              </Grid>
              <Grid item xs={4}></Grid>

              {/* Checkbox Row: Allow Document Handling Duplication / Allow Merger Duplication */}
              <Grid item xs={4}>
                {["OneFolderPerDocument", "OneFolderPerPackage"].includes(documentBundleState?.BundlingOption ?? "") && (
                  <Checkbox
                    sx={{ p: 0, m: 0 }}
                    checked={
                      documentBundleState?.BundlingOption === OneFolderPerDocument
                        ? documentBundleState?.DocumentHandlingDuplicate ?? false
                        : documentBundleState?.MergerConfigDuplicate ?? false
                    }
                    onChange={e =>
                      handlePropChange(
                        documentBundleState?.BundlingOption === OneFolderPerDocument
                          ? "DocumentHandlingDuplicate"
                          : "MergerConfigDuplicate",
                        e.target.checked
                      )
                    }
                  />
                )}
                <span style={{ marginLeft: 8 }}>
                  {documentBundleState?.BundlingOption === OneFolderPerDocument
                    ? "Allow Document Handling Duplication"
                    : "Allow Merger Duplication"}
                </span>
              </Grid>
              <Grid item xs={8}></Grid>

              {/* Divider after form controls */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>


            <Stack direction="column" spacing={1} sx={{mt: 1}}>
              <DtHeader level="subtitle1">
                {title}
              </DtHeader>

              {documentBundleState?.BundlingOption === OneFolderPerDocument ? (
                <DtDataGrid
                  columns={packageConfigColumnsRef(
                    getCalculatedConfigValue,
                    [...documentMapsPrefix, ...documentMappings?.Configuration?.DocumentMaps.map(x => x.DocumentIndex.toString()) ?? []]
                  )}
                  addConfig={{
                    mode: 'inline',
                    label: 'New Config',
                    onAdd: addRow
                  }}
                  editConfig={{
                    mode: 'inline',
                    onEdit: editRow
                  }}
                  deleteConfig={{
                    confirm: false,
                    onDelete: removeRow
                  }}
                  responsivePageSize={true}
                  responsivePageConfig={{heightRatio: 0.7}}
                  actionsConfig={{mode: 'inline', actions: [{type: 'add'}]}}
                  data={documentBundleState?.DocumentHandlingPackageConfigs ?? []}
                  tableName="packageConfigs"
                  dense={true}
                  containerSx={dataGridStyles}
                  paged={true}
                />
              ) : (
                <DtDataGrid
                  columns={mergePackageConfigColumnsRef(
                    [...documentMapsPrefix, ...documentMappings?.Configuration?.DocumentMaps.map(x => x.DocumentIndex) ?? []]
                  )}
                  responsivePageSize={true}
                  responsivePageConfig={{heightRatio: 0.6}}
                  addConfig={{
                    mode: 'inline',
                    label: 'New Config',
                    onAdd: addRow
                  }}
                  editConfig={{
                    mode: 'inline',
                    onEdit: editRow
                  }}
                  deleteConfig={{
                    confirm: false,
                    onDelete: removeRow
                  }}
                  actionsConfig={{mode: 'inline', actions: [{type: 'add'}]}}
                  data={documentBundleState?.MergerPackageConfigs ?? []}
                  tableName="mergepackageConfigs"
                  dense={true}
                  containerSx={dataGridStyles}
                  paged={true}
                />
              )}
            </Stack>

          </Stack>
          
          <StyledSnackbar
            open={alertOpen}
            autoHideDuration={4000}
            onClose={handleSnackBarClose}
          >
            <StyledAlert
              onClose={handleSnackBarClose}
              severity={alertSeverity}>
              {alertMessage}
            </StyledAlert>
          </StyledSnackbar>
        </DtDialog>
      </>
    );
};

export default ConfigDocumentHandlingDialog;